import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Car, ArrowRight, BookOpen, Save, Trash2, Check, Settings2 } from "lucide-react";
import { VehiclePicker } from "../components/VehiclePicker";
import { useVehicle } from "../context/VehicleContext";
import { useUnits } from "../context/UnitsContext";
import { UnitToggle } from "../components/UnitToggle";
import { LiftSelector } from "../components/LiftSelector";
import { CustomVehicleDialog } from "../components/CustomVehicleDialog";
import { VehicleRequest } from "../components/VehicleRequest";
import { LIFT_PRESETS } from "../lib/specs";

const SpecChip = ({ label, spec, isToe }) => {
  const { toeNum, toeSuffix } = useUnits();
  const target = isToe ? `${toeNum(spec.target, true)}${toeSuffix}` : `${spec.target >= 0 ? "+" : ""}${spec.target.toFixed(2)}°`;
  const range = isToe ? `${toeNum(spec.min)} … ${toeNum(spec.max)}${toeSuffix}` : `${spec.min.toFixed(2)} … ${spec.max.toFixed(2)}`;
  return (
  <div className="rounded-lg bg-ink border border-steel/60 p-3 text-center">
    <div className="font-mono text-[9px] text-slate-500 tracking-widest uppercase">{label}</div>
    <div className="font-mono text-sm text-cyan tabular-nums mt-1">{target}</div>
    <div className="font-mono text-[9px] text-slate-500 mt-0.5">{range}</div>
  </div>
  );
};

export default function Garage() {
  const { specs, vehicleLabel, selected, hasVehicle, vinInfo, drivetrain, lift,
          garage, activeGarageId, isCustomActive,
          saveCurrentToGarage, activateGarageVehicle, removeFromGarage } = useVehicle();
  const savedActive = garage.find((e) => e.id === activeGarageId);
  const { t } = useTranslation();
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 animate-rise">
        <div className="flex items-center gap-2 font-mono text-xs text-cyan tracking-widest uppercase">
          <Car size={14} /> {t("garage.kicker")}
        </div>
        <h1 className="font-heading font-extrabold uppercase tracking-wide text-3xl sm:text-4xl lg:text-5xl mt-2">{t("garage.title")}</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">{t("garage.subtitle")}</p>
      </div>

      <div className="space-y-6">
        {garage.length > 0 && (
          <div className="rounded-2xl bg-surface border border-steel p-5 animate-rise" data-testid="saved-garage">
            <div className="flex items-center gap-2 mb-3">
              <Car size={16} className="text-cyan" />
              <h2 className="font-heading font-bold uppercase tracking-widest text-sm">{t("garage.saved")}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {garage.map((e) => {
                const active = e.id === activeGarageId;
                return (
                  <div key={e.id} data-testid={`garage-item-${e.id}`}
                    className={`rounded-xl border p-3 flex items-center justify-between gap-2 cursor-pointer transition-colors ${active ? "border-cyan bg-cyan/10" : "border-steel bg-ink hover:border-cyan/50"}`}
                    onClick={() => activateGarageVehicle(e.id)}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {active && <Check size={13} className="text-cyan shrink-0" />}
                        <span className="font-heading text-sm text-white truncate">{e.label}</span>
                      </div>
                      <div className="flex gap-1.5 mt-1">
                        <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{e.source === "custom" ? t("garage.custom") : t("garage.catalog")}</span>
                        {e.lift && e.lift !== "stock" && <span className="font-mono text-[9px] uppercase tracking-widest text-amber">{LIFT_PRESETS[e.lift]?.label}</span>}
                      </div>
                    </div>
                    <button data-testid={`garage-remove-${e.id}`} onClick={(ev) => { ev.stopPropagation(); removeFromGarage(e.id); }}
                      className="text-slate-500 hover:text-fail shrink-0 p-1" aria-label="Remove">
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <VehiclePicker />

        <div className="flex flex-wrap gap-3">
          <CustomVehicleDialog />
          {selected && !isCustomActive && (
            <button data-testid="save-garage-btn" onClick={saveCurrentToGarage}
              className="inline-flex items-center gap-2 rounded-xl bg-elevated border border-steel text-slate-200 px-4 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-steel transition-colors">
              <Save size={15} /> {savedActive ? t("garage.savedBtn") : t("garage.saveBtn")}
            </button>
          )}
          <VehicleRequest />
        </div>

        {hasVehicle ? (
        <div className="rounded-2xl bg-surface border border-steel p-5 animate-rise" data-testid="garage-summary">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold uppercase tracking-widest text-base">{vehicleLabel}</h2>
            <span className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">{drivetrain}</span>
          </div>

          {vinInfo && (vinInfo.trim || vinInfo.engine || vinInfo.fuel || vinInfo.bodyClass) && (
            <div className="mb-4 flex flex-wrap gap-2" data-testid="vin-details">
              {[
                ["Trim", vinInfo.trim],
                ["Engine", vinInfo.engine],
                ["Fuel", vinInfo.fuel],
                ["Drive", vinInfo.drive],
                ["Body", vinInfo.bodyClass],
              ].filter(([, v]) => v).map(([k, v]) => (
                <span key={k} data-testid={`vin-chip-${k.toLowerCase()}`}
                  className="inline-flex items-center gap-1.5 rounded-md bg-ink border border-steel/60 px-2.5 py-1 font-mono text-[10px] tracking-wide">
                  <span className="text-slate-500 uppercase">{k}</span>
                  <span className="text-cyan">{v}</span>
                </span>
              ))}
            </div>
          )}

          <div className="mb-4 space-y-3">
            <UnitToggle />
            <LiftSelector />
          </div>

          <div className="font-mono text-[10px] text-cyan tracking-widest uppercase mb-2">{t("common.front")}</div>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <SpecChip label={t("common.camber")} spec={specs.front.camber} />
            <SpecChip label={t("common.caster")} spec={specs.front.caster} />
            <SpecChip label={t("common.totalToe")} spec={specs.front.totalToe} isToe />
          </div>

          <div className="font-mono text-[10px] text-cyan tracking-widest uppercase mb-2">{t("common.rear")}</div>
          <div className="grid grid-cols-2 gap-3">
            <SpecChip label={t("common.camber")} spec={specs.rear.camber} />
            <SpecChip label={t("common.totalToe")} spec={specs.rear.totalToe} isToe />
          </div>
        </div>
        ) : (
          <div data-testid="garage-empty" className="rounded-2xl border border-amber/40 bg-amber/5 p-6 text-center animate-rise">
            <p className="text-sm text-slate-300">{t("garage.empty")}</p>
          </div>
        )}

        {hasVehicle && (
        <div className="flex flex-wrap gap-3 animate-rise">
          <Link to="/app" data-testid="garage-go-align"
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan to-blue-600 text-ink px-5 py-3 font-heading font-bold text-sm tracking-wide shadow-lg shadow-cyan/25">
            {t("garage.startAlign")} <ArrowRight size={15} />
          </Link>
          <Link to="/specs" data-testid="garage-go-specs"
            className="inline-flex items-center gap-2 rounded-xl bg-elevated border border-steel text-slate-200 px-5 py-3 font-mono text-xs uppercase tracking-widest hover:bg-steel transition-colors">
            <BookOpen size={15} /> {t("garage.specVault")}
          </Link>
        </div>
        )}

        <div className="rounded-xl border border-amber/40 bg-amber/5 p-4">
          <p className="text-xs text-slate-300 leading-relaxed">
            Catalog values are representative factory reference figures for common vehicles. Always confirm against the manufacturer's service information for your exact trim and build before final torque.
          </p>
        </div>
      </div>
    </div>
  );
}
